import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Key,
  Plus,
  Search,
  Ban,
  RotateCcw,
  Copy,
  Monitor,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  FileText,
  Download,
  RefreshCw,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  active: "bg-green-500/20 text-green-400 border-green-500/30",
  revoked: "bg-red-500/20 text-red-400 border-red-500/30",
  expired: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  deactivated: "bg-gray-500/20 text-gray-400 border-gray-500/30",
};

const planColors = {
  trial: "bg-gray-500/20 text-gray-400",
  pro: "bg-green-500/20 text-green-400",
  enterprise: "bg-purple-500/20 text-purple-400",
};

export default function PluginLicensesPanel() {
  const [licenses, setLicenses] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stats, setStats] = useState({ total: 0, active: 0, revoked: 0 });
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showCreatedDialog, setShowCreatedDialog] = useState(false);
  
  // Form data
  const [newLicense, setNewLicense] = useState({
    email: "",
    product_id: "AVXPROVintageStrip",
    plan: "pro",
    max_machines: 3,
    expiration_type: "permanent",
  });
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [revokeReason, setRevokeReason] = useState("");
  const [createdKey, setCreatedKey] = useState("");
  const [processing, setProcessing] = useState(false);

  const fetchLicenses = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ limit: "100" });
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (searchTerm) params.append("email", searchTerm);

      const response = await fetch(
        `${API_URL}/api/admin/plugin-licenses/list?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error("Error al cargar licencias");
      
      const data = await response.json();
      setLicenses(data.licenses || []);
      
      // Calculate stats
      const active = data.licenses?.filter(l => l.status === "active").length || 0;
      const revoked = data.licenses?.filter(l => l.status === "revoked").length || 0;
      setStats({ total: data.total || 0, active, revoked });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm]);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/admin/plugin-licenses/audit-logs?limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error("Error al cargar logs");
      
      const data = await response.json();
      setAuditLogs(data.logs || []);
    } catch (error) {
      // Silent fail for audit logs
    }
  }, []);

  useEffect(() => {
    fetchLicenses();
    fetchAuditLogs();
  }, [fetchLicenses, fetchAuditLogs]);

  const handleCreateLicense = async () => {
    if (!newLicense.email) {
      toast.error("El email es requerido");
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem("token");
      const body = {
        email: newLicense.email,
        product_id: newLicense.product_id,
        plan: newLicense.plan,
        max_machines: parseInt(newLicense.max_machines),
      };
      
      // Calcular fecha de expiración según tipo seleccionado
      if (newLicense.expiration_type === "15days") {
        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + 15);
        body.expires_at = expiresAt.toISOString();
      }
      // Si es "permanent", no enviamos expires_at (null = sin expiración)

      const response = await fetch(`${API_URL}/api/admin/plugin-licenses/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Error al crear licencia");
      }

      const data = await response.json();
      setCreatedKey(data.license_key);
      setShowCreateDialog(false);
      setShowCreatedDialog(true);
      setNewLicense({
        email: "",
        product_id: "AVXPROVintageStrip",
        plan: "pro",
        max_machines: 3,
        expiration_type: "permanent",
      });
      fetchLicenses();
      fetchAuditLogs();
      toast.success("Licencia creada exitosamente");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleRevokeLicense = async () => {
    if (!selectedLicense) return;

    setProcessing(true);
    try {
      const token = localStorage.getItem("token");
      
      // We need the license_key but we only have the hash in the list
      // The admin needs to input the key or we use a special admin endpoint
      const response = await fetch(`${API_URL}/api/admin/plugin-licenses/revoke`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          license_key: selectedLicense.license_key_input,
          reason: revokeReason,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Error al revocar licencia");
      }

      toast.success("Licencia revocada");
      setShowRevokeDialog(false);
      setSelectedLicense(null);
      setRevokeReason("");
      fetchLicenses();
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleResetMachines = async () => {
    if (!selectedLicense) return;

    setProcessing(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/plugin-licenses/reset-machines`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          license_key: selectedLicense.license_key_input,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Error al resetear máquinas");
      }

      const data = await response.json();
      toast.success(`${data.machines_deactivated} máquina(s) desactivada(s)`);
      setShowResetDialog(false);
      setSelectedLicense(null);
      fetchLicenses();
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado al portapapeles");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("es-CL", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const exportCSV = () => {
    const headers = ["Email", "Producto", "Plan", "Estado", "Máquinas", "Creada", "Expira"];
    const rows = licenses.map(l => [
      l.email,
      l.product_id,
      l.plan,
      l.status,
      `${l.machines_used || 0}/${l.max_machines}`,
      formatDate(l.created_at),
      l.expires_at ? formatDate(l.expires_at) : "Sin expiración",
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `plugin_licenses_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="plugin-licenses-panel">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gray-900/50 border-gray-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Licencias</p>
                <p className="text-3xl font-bold text-white">{stats.total}</p>
              </div>
              <Key className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 border-gray-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Activas</p>
                <p className="text-3xl font-bold text-green-400">{stats.active}</p>
              </div>
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 border-gray-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Revocadas</p>
                <p className="text-3xl font-bold text-red-400">{stats.revoked}</p>
              </div>
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="licenses" className="space-y-4">
        <TabsList className="bg-gray-800 border border-gray-700">
          <TabsTrigger value="licenses" className="data-[state=active]:bg-green-600">
            <Key className="w-4 h-4 mr-2" />
            Licencias
          </TabsTrigger>
          <TabsTrigger value="audit" className="data-[state=active]:bg-green-600">
            <FileText className="w-4 h-4 mr-2" />
            Audit Logs
          </TabsTrigger>
        </TabsList>

        {/* Licenses Tab */}
        <TabsContent value="licenses" className="space-y-4">
          {/* Actions Bar */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar por email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-gray-800 border-gray-700 w-full sm:w-64"
                  data-testid="license-search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40 bg-gray-800 border-gray-700">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="active">Activas</SelectItem>
                  <SelectItem value="revoked">Revocadas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={exportCSV}
                className="border-gray-600"
              >
                <Download className="w-4 h-4 mr-2" />
                CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { fetchLicenses(); fetchAuditLogs(); }}
                className="border-gray-600"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="create-license-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nueva Licencia
              </Button>
            </div>
          </div>

          {/* Licenses Table */}
          <Card className="bg-gray-900/50 border-gray-700">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-700 hover:bg-transparent">
                    <TableHead className="text-gray-400">Email</TableHead>
                    <TableHead className="text-gray-400">Producto</TableHead>
                    <TableHead className="text-gray-400">Plan</TableHead>
                    <TableHead className="text-gray-400">Estado</TableHead>
                    <TableHead className="text-gray-400">Máquinas</TableHead>
                    <TableHead className="text-gray-400">Key (últimos 4)</TableHead>
                    <TableHead className="text-gray-400">Creada</TableHead>
                    <TableHead className="text-gray-400">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {licenses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-gray-400 py-8">
                        No hay licencias registradas
                      </TableCell>
                    </TableRow>
                  ) : (
                    licenses.map((license) => (
                      <TableRow key={license.id} className="border-gray-700" data-testid={`license-row-${license.id}`}>
                        <TableCell className="text-white font-medium">
                          {license.email}
                        </TableCell>
                        <TableCell className="text-gray-300">
                          <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30">
                            {license.product_id}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={planColors[license.plan] || planColors.pro}>
                            {license.plan}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusColors[license.status]}>
                            {license.status === "active" && <CheckCircle2 className="w-3 h-3 mr-1" />}
                            {license.status === "revoked" && <XCircle className="w-3 h-3 mr-1" />}
                            {license.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-300">
                          <div className="flex items-center gap-1">
                            <Monitor className="w-4 h-4 text-gray-500" />
                            {license.machines_used || 0}/{license.max_machines}
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-400 font-mono">
                          ****{license.license_key_last4}
                        </TableCell>
                        <TableCell className="text-gray-400 text-sm">
                          {formatDate(license.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {license.status === "active" && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedLicense({ ...license, license_key_input: "" });
                                    setShowResetDialog(true);
                                  }}
                                  className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10"
                                  title="Reset máquinas"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedLicense({ ...license, license_key_input: "" });
                                    setShowRevokeDialog(true);
                                  }}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  title="Revocar"
                                >
                                  <Ban className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Logs Tab */}
        <TabsContent value="audit" className="space-y-4">
          <Card className="bg-gray-900/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-gray-400" />
                Últimos 50 eventos
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-700 hover:bg-transparent">
                    <TableHead className="text-gray-400">Fecha</TableHead>
                    <TableHead className="text-gray-400">Evento</TableHead>
                    <TableHead className="text-gray-400">Email</TableHead>
                    <TableHead className="text-gray-400">IP</TableHead>
                    <TableHead className="text-gray-400">Resultado</TableHead>
                    <TableHead className="text-gray-400">Detalle</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLogs.map((log, idx) => (
                    <TableRow key={idx} className="border-gray-700">
                      <TableCell className="text-gray-400 text-sm">
                        {formatDate(log.created_at)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-gray-700/50 text-gray-300">
                          {log.event}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-300">{log.email || "—"}</TableCell>
                      <TableCell className="text-gray-400 font-mono text-sm">
                        {log.ip || "—"}
                      </TableCell>
                      <TableCell>
                        <Badge className={log.result === "success" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                          {log.result}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-400 text-sm max-w-xs truncate">
                        {log.detail || "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create License Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-green-500" />
              Crear Nueva Licencia
            </DialogTitle>
            <DialogDescription>
              Genera una licencia para el plugin AVXPROVintageStrip
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Email del cliente *</Label>
              <Input
                type="email"
                placeholder="cliente@email.com"
                value={newLicense.email}
                onChange={(e) => setNewLicense({ ...newLicense, email: e.target.value })}
                className="bg-gray-800 border-gray-700"
                data-testid="new-license-email"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Producto</Label>
                <Select
                  value={newLicense.product_id}
                  onValueChange={(v) => setNewLicense({ ...newLicense, product_id: v })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AVXPROVintageStrip">AVXPROVintageStrip</SelectItem>
                    <SelectItem value="AVXPROCompressor">AVXPROCompressor</SelectItem>
                    <SelectItem value="AVXPROEqualizer">AVXPROEqualizer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Plan</Label>
                <Select
                  value={newLicense.plan}
                  onValueChange={(v) => setNewLicense({ ...newLicense, plan: v })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial (14 días)</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Máquinas máx.</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={newLicense.max_machines}
                  onChange={(e) => setNewLicense({ ...newLicense, max_machines: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Expiración</Label>
                <Select
                  value={newLicense.expiration_type}
                  onValueChange={(v) => setNewLicense({ ...newLicense, expiration_type: v })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15days">15 días</SelectItem>
                    <SelectItem value="permanent">Permanente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateLicense}
              disabled={processing}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-create-license"
            >
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Crear Licencia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Created License Dialog (shows the key) */}
      <Dialog open={showCreatedDialog} onOpenChange={setShowCreatedDialog}>
        <DialogContent className="bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Licencia Creada
            </DialogTitle>
            <DialogDescription>
              Guarda esta clave - no se mostrará de nuevo
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6">
            <div className="bg-gray-800 rounded-lg p-4 flex items-center justify-between">
              <code className="text-xl font-mono text-green-400" data-testid="created-license-key">
                {createdKey}
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(createdKey)}
                className="text-gray-400 hover:text-white"
              >
                <Copy className="w-5 h-5" />
              </Button>
            </div>
            <p className="text-yellow-400 text-sm mt-4 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Esta clave no se puede recuperar. Cópiala ahora.
            </p>
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => setShowCreatedDialog(false)}
              className="bg-green-600 hover:bg-green-700"
            >
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke License Dialog */}
      <Dialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
        <DialogContent className="bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-500" />
              Revocar Licencia
            </DialogTitle>
            <DialogDescription>
              Esta acción desactivará todas las máquinas y bloqueará la licencia
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <p className="text-red-400 text-sm">
                <strong>Email:</strong> {selectedLicense?.email}
              </p>
              <p className="text-red-400 text-sm">
                <strong>Key:</strong> ****{selectedLicense?.license_key_last4}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-300">License Key completa *</Label>
              <Input
                placeholder="AVX-XXXX-XXXX-XXXX"
                value={selectedLicense?.license_key_input || ""}
                onChange={(e) => setSelectedLicense({ ...selectedLicense, license_key_input: e.target.value.toUpperCase() })}
                className="bg-gray-800 border-gray-700 font-mono"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-300">Razón (opcional)</Label>
              <Input
                placeholder="Motivo de la revocación"
                value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value)}
                className="bg-gray-800 border-gray-700"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevokeDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleRevokeLicense}
              disabled={processing || !selectedLicense?.license_key_input}
              className="bg-red-600 hover:bg-red-700"
            >
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Revocar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Machines Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-yellow-500" />
              Reset de Máquinas
            </DialogTitle>
            <DialogDescription>
              Desactivará todas las máquinas permitiendo nuevas activaciones
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <p className="text-yellow-400 text-sm">
                <strong>Email:</strong> {selectedLicense?.email}
              </p>
              <p className="text-yellow-400 text-sm">
                <strong>Máquinas activas:</strong> {selectedLicense?.machines_used || 0}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-300">License Key completa *</Label>
              <Input
                placeholder="AVX-XXXX-XXXX-XXXX"
                value={selectedLicense?.license_key_input || ""}
                onChange={(e) => setSelectedLicense({ ...selectedLicense, license_key_input: e.target.value.toUpperCase() })}
                className="bg-gray-800 border-gray-700 font-mono"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleResetMachines}
              disabled={processing || !selectedLicense?.license_key_input}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
